import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, FlaskConical } from "lucide-react";
import { LABELS, getIssue, sortedNewsletters } from "@/lib/newsletters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const headers = () => ({ "X-Admin-Password": sessionStorage.getItem("herus-admin-pw") });
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");

const issuePayload = (slug, lang) => {
  const n = getIssue(slug, lang);
  return n && { number: n.number, title: n.title, lead: n.lead, cover: n.cover, series: LABELS[lang].series };
};

function IssueRow({ n, send, onSend, busy }) {
  const done = send && send.status === "done";
  return (
    <li data-testid={`send-row-${n.slug}`} className="py-5 flex flex-wrap items-center justify-between gap-4 border-b border-[#0A0A0A]/10">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">{n.number} · {n.date}</p>
        <p className="mt-1 font-serif-display text-lg text-[#0A0A0A]">{n.title}</p>
        {send && (
          <p data-testid={`send-status-${n.slug}`} className="mt-1 text-xs text-[#0A0A0A]/60">
            {done ? `Envoyé le ${fmt(send.sent_at)} : ${send.sent} réussi(s), ${send.failed} échec(s)` : `Envoi en cours… ${send.sent || 0} / ${send.total}`}
          </p>
        )}
      </div>
      <div className="flex gap-3">
        <button onClick={() => onSend(n.slug, true)} disabled={busy} data-testid={`send-test-${n.slug}`} className="inline-flex items-center gap-2 border border-[#0A0A0A]/20 text-[#0A0A0A] text-xs uppercase tracking-[0.15em] px-4 py-3 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50">
          <FlaskConical size={14} /> Envoi test
        </button>
        <button onClick={() => onSend(n.slug, false)} disabled={busy || !!send} data-testid={`send-all-${n.slug}`} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F9F6F0] text-xs uppercase tracking-[0.15em] px-4 py-3 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {send ? "Déjà envoyé" : "Envoyer aux abonnés"}
        </button>
      </div>
    </li>
  );
}

export const SubscribersPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => axios.get(`${API}/subscribers`, { headers: headers() }).then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.sends.some((s) => s.status === "sending")) return;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [data, load]);

  const onSend = async (slug, test) => {
    const active = data.subscribers.filter((s) => s.status === "active").length;
    if (!test && !window.confirm(`Envoyer ce numéro à ${active} abonné(s), chacun dans sa langue ? Cette action est définitive.`)) return;
    setBusy(slug);
    try {
      const r = await axios.post(`${API}/newsletters/${slug}/send`, { fr: issuePayload(slug, "fr"), en: issuePayload(slug, "en"), test }, { headers: headers() });
      toast.success(test ? `Email(s) de test envoyé(s) à ${r.data.to} (FR + EN).` : `Envoi lancé à ${r.data.total} abonné(s).`);
      if (!test) setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'envoi a échoué.");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <Loader2 className="mt-12 animate-spin text-[#C5A059]" />;
  const subs = data.subscribers;
  const active = subs.filter((s) => s.status === "active");
  const sendFor = (slug) => data.sends.find((s) => s.slug === slug);

  return (
    <div className="mt-10 text-[#0A0A0A]">
      <div className="grid sm:grid-cols-3 gap-6">
        {[["Abonnés actifs", active.length, "subs-active"], ["Français / Anglais", `${active.filter((s) => s.lang !== "en").length} / ${active.filter((s) => s.lang === "en").length}`, "subs-langs"], ["Désabonnés", subs.length - active.length, "subs-unsub"]].map(([l, v, t]) => (
          <div key={t} className="border border-[#0A0A0A]/10 p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/50">{l}</p>
            <p data-testid={t} className="mt-3 font-serif-display text-4xl text-[#C5A059]">{v}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-16 text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">Envoyer un numéro</h2>
      <p className="mt-2 text-sm text-[#0A0A0A]/60">L'envoi test part sur votre adresse en français et en anglais. L'envoi aux abonnés n'est possible qu'une fois par numéro.</p>
      <ul className="mt-4">
        {sortedNewsletters().map((n) => <IssueRow key={n.slug} n={n} send={sendFor(n.slug)} onSend={onSend} busy={busy === n.slug} />)}
      </ul>

      <h2 className="mt-16 text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">Liste des abonnés</h2>
      <ul data-testid="subscribers-list" className="mt-6 divide-y divide-[#0A0A0A]/10 text-sm">
        {subs.map((s) => (
          <li key={s.id} className={`py-3 flex flex-wrap justify-between gap-2 ${s.status !== "active" ? "opacity-50 line-through" : ""}`}>
            <span>{s.email}</span>
            <span className="text-[#0A0A0A]/60">{s.lang.toUpperCase()} · {fmt(s.created_at)}</span>
          </li>
        ))}
        {!subs.length && <li className="py-3 text-[#0A0A0A]/50">Aucun abonné pour le moment.</li>}
      </ul>
    </div>
  );
};
